(() => {
  const MODULE_FLAG = '__abokContactModuleInitialized';
  const REQUEST_SET = '__abokContactKlaviyoRequests';
  const STORAGE_TTL_MS = 15 * 60 * 1000;
  const FORM_CONFIG = {
    support: {
      metric: 'Customer Support Submitted',
      properties: ['help_topic', 'message', 'privacy_consent']
    },
    business: {
      metric: 'Business Inquiry Submitted',
      properties: ['company', 'country_or_region', 'inquiry_type', 'project_summary', 'privacy_consent']
    }
  };

  window[REQUEST_SET] = window[REQUEST_SET] || new Set();
  const requestIds = window[REQUEST_SET];

  const createUniqueId = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  };

  const getStorageKey = (sectionId, formType) => `abok-contact:${sectionId}:${formType}`;

  const clearStoredPayload = (key) => {
    try {
      window.sessionStorage.removeItem(key);
    } catch (_error) {
      // Session Storage is optional progressive enhancement.
    }
  };

  const readStoredPayload = (key) => {
    try {
      const payload = JSON.parse(window.sessionStorage.getItem(key) || 'null');
      if (!payload || !Number.isFinite(payload.expiresAt) || payload.expiresAt <= Date.now()) {
        clearStoredPayload(key);
        return null;
      }
      return payload;
    } catch (_error) {
      clearStoredPayload(key);
      return null;
    }
  };

  const writeStoredPayload = (key, payload) => {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(payload));
    } catch (_error) {
      // Shopify Contact Form submission must continue when storage is unavailable.
    }
  };

  const getPageUrl = () => {
    const url = new URL(window.location.href);
    return `${url.origin}${url.pathname}`;
  };

  const collectPayload = (form, formType) => {
    const config = FORM_CONFIG[formType];
    if (!config) return null;

    const email = form.querySelector('[data-klaviyo-profile="email"]')?.value.trim() || '';
    if (!email) return null;

    const properties = {
      form_type: formType,
      form_id: form.id,
      page_url: getPageUrl(),
      submitted_at: new Date().toISOString()
    };

    config.properties.forEach((propertyName) => {
      const field = form.querySelector(`[data-klaviyo-property="${propertyName}"]`);
      if (!field) return;

      if (field instanceof HTMLInputElement && field.type === 'checkbox') {
        properties[propertyName] = field.checked;
        return;
      }

      const value = String(field.value).trim();
      if (value) properties[propertyName] = value;
    });

    return {
      email,
      metric: config.metric,
      uniqueId: createUniqueId(),
      properties,
      expiresAt: Date.now() + STORAGE_TTL_MS
    };
  };

  const sendKlaviyoEvent = async ({ payload, storageKey, formType, companyId, revision }) => {
    if (!payload?.email || !payload?.metric || !payload?.uniqueId || requestIds.has(payload.uniqueId)) return;

    requestIds.add(payload.uniqueId);
    let response;

    try {
      response = await fetch(
        `https://a.klaviyo.com/client/events/?company_id=${encodeURIComponent(companyId)}`,
        {
          method: 'POST',
          headers: {
            accept: 'application/vnd.api+json',
            'content-type': 'application/vnd.api+json',
            revision
          },
          body: JSON.stringify({
            data: {
              type: 'event',
              attributes: {
                unique_id: payload.uniqueId,
                properties: payload.properties,
                metric: {
                  data: {
                    type: 'metric',
                    attributes: { name: payload.metric }
                  }
                },
                profile: {
                  data: {
                    type: 'profile',
                    attributes: { email: payload.email }
                  }
                }
              }
            }
          }),
          keepalive: true
        }
      );

      if (!response.ok) throw new Error('http_error');
      clearStoredPayload(storageKey);
    } catch (error) {
      requestIds.delete(payload.uniqueId);
      console.warn('ABOK Contact: unable to send the optional Klaviyo event.', {
        formType,
        status: response?.status || 0,
        errorType: error?.message === 'http_error' ? 'http_error' : 'network_error'
      });
    }
  };

  const getSubmittedFormId = () => {
    try {
      return decodeURIComponent(window.location.hash.slice(1));
    } catch (_error) {
      return window.location.hash.slice(1);
    }
  };

  const showResultModal = (wrapper, form) => {
    const hasResult = wrapper.dataset.postedSuccessfully === 'true' || wrapper.dataset.hasErrors === 'true';
    if (!hasResult || getSubmittedFormId() !== form.id) return false;

    const modal = wrapper.closest('modal-element');
    if (!modal) return true;

    customElements.whenDefined('modal-element').then(() => {
      if (!modal.isConnected || typeof modal.show !== 'function') return;

      const status = wrapper.querySelector('.contact-abok-form__status');
      if (status) {
        modal.addEventListener('modal:afterShow', () => status.focus(), { once: true });
      }
      modal.show();
    });
    return true;
  };

  const initializeSection = (section) => {
    if (section.dataset.contactAbokInitialized === 'true') return;
    section.dataset.contactAbokInitialized = 'true';

    const sectionId = section.dataset.sectionId || '';
    const klaviyoEnabled = section.dataset.klaviyoEnabled === 'true';
    const companyId = section.dataset.klaviyoCompanyId?.trim() || '';
    const revision = section.dataset.klaviyoRevision?.trim() || '';
    const canSendKlaviyo = klaviyoEnabled && companyId !== '' && /^\d{4}-\d{2}-\d{2}(?:\.[A-Za-z0-9_-]+)?$/.test(revision);

    section.querySelectorAll('[data-contact-abok-form]').forEach((wrapper) => {
      const form = wrapper.closest('form');
      const formType = wrapper.dataset.contactAbokForm;
      if (!form || !FORM_CONFIG[formType]) return;

      const storageKey = getStorageKey(sectionId, formType);

      form.addEventListener('submit', () => {
        if (!canSendKlaviyo || !form.checkValidity()) return;
        const payload = collectPayload(form, formType);
        if (payload) writeStoredPayload(storageKey, payload);
      });

      const isMatchingResult = showResultModal(wrapper, form);
      if (!canSendKlaviyo) {
        clearStoredPayload(storageKey);
        return;
      }

      if (isMatchingResult && wrapper.dataset.postedSuccessfully === 'true') {
        const payload = readStoredPayload(storageKey);
        if (payload) {
          sendKlaviyoEvent({ payload, storageKey, formType, companyId, revision });
        }
      } else {
        readStoredPayload(storageKey);
      }
    });
  };

  const initializeAll = (root = document) => {
    if (root.matches?.('[data-contact-abok]')) initializeSection(root);
    root.querySelectorAll?.('[data-contact-abok]').forEach(initializeSection);
  };

  if (window[MODULE_FLAG]) {
    initializeAll();
    return;
  }

  window[MODULE_FLAG] = true;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
